import ChecklistQuestionScope from "./ChecklistQuestionScope";
import { canContributeToReview } from "./reviewPermissions";
import "./InspectionChecklistSelection.scss";
import ObservationFindingConversion from "../../components/Inspections/ObservationFindingConversion";
import { getData } from "../../helpers/url-request";
import ObservationsPanel from "../../components/Inspections/ObservationsPanel";
import { usePiersAssistantContext } from "../../components/Assistant/PiersAssistant";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Modal, ModalBody } from "../../components/Common/AppOverlay";
import CustomDataGrid from "../../components/Common/CustomDataGrid";
import { ErrorNotify, SuccessNotify } from "../../shared/Alert";
import {
  addInspectionChecklists,
  approveInspection,
  downloadInspectionDocument,
  getChecklists,
  getInspectionActivities,
  getInspectionApprovalPermission,
  getInspectionChecklist,
  getInspectionChecklistQueries,
  getInspectionEvidenceCount,
  getInspectionFindings,
  getInspectionGeneratedReports,
  getInspectionReviewDocuments,
  getInspectionReviewWorkflowTasks,
  getLoggedInUser,
  previewInspectionDocument,
  reassignInspectionReviewWorkflowTask,
  respondToInspectionChecklistQuery,
  returnInspectionForChanges,
  saveInspectionFindingManagementResponse,
  startInspectionReview,
  uploadInspectionEvidence,
  uploadInspectionReviewDocuments,
} from "../../helpers/backend_helper";
import { showLoading } from "react-global-loading";
import WorkflowResponseDrawer from "../../components/Inspections/WorkflowResponseDrawer";
import "./InspectionReviewWorkspace.scss";

const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "—";

const dateLabel = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

const timeLabel = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const relativeTime = (value) => {
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return "No recent update";
  const minutes = Math.max(0, Math.round((Date.now() - time) / 60000));
  if (minutes < 1) return "Updated just now";
  if (minutes < 60) return `Updated ${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `Updated ${hours} hr ago`;
  return `Updated ${Math.round(hours / 24)} day${
    Math.round(hours / 24) === 1 ? "" : "s"
  } ago`;
};

const dueNote = (value) => {
  if (!value) return "No due date";
  const days = Math.ceil(
    (new Date(value).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) /
      86400000
  );
  if (Number.isNaN(days)) return "Due date unavailable";
  if (days < 0)
    return `${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} overdue`;
  if (days === 0) return "Due today";
  return `Due in ${days} day${days === 1 ? "" : "s"}`;
};

const isAnswered = (question) =>
  Boolean(question.inspectionResponse?.trim()) ||
  Number(question.percentage) === 100 ||
  Number(question.inspectionStatusId) === 3;

const questionAnswer = (question) => {
  if (!isAnswered(question)) return { label: "In Progress", tone: "warning" };
  const response = String(question.inspectionResponse || "")
    .trim()
    .toLowerCase();
  if (response === "yes") return { label: "Yes", tone: "success" };
  if (response === "no") return { label: "No", tone: "danger" };
  if (response === "not applicable") return { label: "N/A", tone: "neutral" };
  return { label: question.inspectionResponse || "Answered", tone: "success" };
};

const activityTone = (note = "") =>
  /query/i.test(note)
    ? "warning"
    : /finding/i.test(note)
    ? "danger"
    : /attach|evidence/i.test(note)
    ? "blue"
    : "success";

const documentIcon = (fileName = "") => {
  const extension = fileName.split(".").pop()?.toLowerCase();
  if (extension === "pdf") return "bx-file-pdf";
  if (["xls", "xlsx", "csv"].includes(extension)) return "bx-spreadsheet";
  if (["png", "jpg", "jpeg"].includes(extension)) return "bx-image";
  if (["ppt", "pptx"].includes(extension)) return "bx-slideshow";
  return "bx-file";
};

const InspectionReviewWorkspace = ({
  dataDetail,
  closeAction,
  backPath,
  backLabel,
  openChecklistAction,
  canStart = false,
  canOpenReadOnly = false,
  canManage = false,
  users = [],
  focusedWorkflowTaskId = null,
  initialScopeChecklistId = null,
}) => {
  const [assignmentMessageOpen, setAssignmentMessageOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(initialScopeChecklistId ? "sections" : "overview");
  const [scopeChecklistId, setScopeChecklistId] = useState(initialScopeChecklistId);
  useEffect(() => {
    if (initialScopeChecklistId) { setActiveTab("sections"); setScopeChecklistId(initialScopeChecklistId); }
  }, [initialScopeChecklistId]);
  const [expanded, setExpanded] = useState(false);
  const [checklistItems, setChecklistItems] = useState([]);
  const [checklistCatalog, setChecklistCatalog] = useState([]);
  const [addChecklistsOpen, setAddChecklistsOpen] = useState(false);
  const [selectedChecklistIds, setSelectedChecklistIds] = useState([]);
  const [addingChecklists, setAddingChecklists] = useState(false);
  const [queries, setQueries] = useState([]);
  const [findings, setFindings] = useState([]);
  const [activities, setActivities] = useState([]);
  const [evidenceCount, setEvidenceCount] = useState(0);
  const [generatedReports, setGeneratedReports] = useState([]);
  const [reviewDocuments, setReviewDocuments] = useState([]);
  const [documentUploadOpen, setDocumentUploadOpen] = useState(false);
  const [documentFiles, setDocumentFiles] = useState([]);
  const [documentCategory, setDocumentCategory] = useState("WorkPaper");
  const [documentDescription, setDocumentDescription] = useState("");
  const [uploadingDocuments, setUploadingDocuments] = useState(false);
  const [reportActionId, setReportActionId] = useState(null);
  const [findingSearch, setFindingSearch] = useState("");
  const [querySearch, setQuerySearch] = useState("");
  const [queryStatus, setQueryStatus] = useState("");
  const [findingStatus, setFindingStatus] = useState("");
  const [taskSearch, setTaskSearch] = useState("");
  const [taskStatus, setTaskStatus] = useState("");
  const [checklistSearch, setChecklistSearch] = useState("");
  const [checklistStatus, setChecklistStatus] = useState("");
  const [workspaceRefresh, setWorkspaceRefresh] = useState(0);
  const [refreshingWorkspace, setRefreshingWorkspace] = useState(false);
  const [approvalPermission, setApprovalPermission] = useState({
    canApprove: false,
    approvers: [],
  });
  const [workflowTasks, setWorkflowTasks] = useState([]);
  const [workflowTaskToReassign, setWorkflowTaskToReassign] = useState(null);
  const [workflowAssignee, setWorkflowAssignee] = useState("");
  const [reassigningWorkflowTask, setReassigningWorkflowTask] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [responseDrawer, setResponseDrawer] = useState(null);
  const [responseText, setResponseText] = useState("");
  const [responseAttachments, setResponseAttachments] = useState([]);
  const [uploadingResponseAttachment, setUploadingResponseAttachment] =
    useState(false);
  const [savingResponse, setSavingResponse] = useState(false);
  const focusedTaskRef = useRef("");
  const documentUploadRef = useRef(null);

  const review = dataDetail || {};
  const inspectionId = review.id;
  const [convertingObservation, setConvertingObservation] = useState(null);
  const [observationRefresh, setObservationRefresh] = useState(0);
  const [observationCount, setObservationCount] = useState(0);
  useEffect(() => { let active = true; getData(`/inspection/${inspectionId}/observations`).then((response) => { if (active && response?.status) setObservationCount((response.data || []).filter((item) => !item.convertedFindingId && item.assessmentStatus !== "Closed").length); }).catch(() => {}); return () => { active = false; }; }, [inspectionId]);
  usePiersAssistantContext({
    screen: "Review",
    reviewId: Number(inspectionId),
  });

  useEffect(() => {
    getLoggedInUser()
      .then((user) => setCurrentUser(user || null))
      .catch(() => setCurrentUser(null));
  }, []);

  useEffect(() => {
    if (!inspectionId) return;
    let active = true;
    if (!refreshingWorkspace) showLoading(true);
    Promise.all([
      getInspectionChecklist(inspectionId),
      getChecklists().catch(() => ({ status: false, data: [] })),
      getInspectionChecklistQueries(inspectionId),
      getInspectionFindings(inspectionId),
      getInspectionActivities(inspectionId),
      getInspectionGeneratedReports(inspectionId),
      getInspectionReviewDocuments(inspectionId).catch(() => ({
        status: false,
        data: [],
      })),
      getInspectionApprovalPermission(inspectionId),
      getInspectionReviewWorkflowTasks(inspectionId),
      getInspectionEvidenceCount(inspectionId).catch(() => ({ status: false, data: 0 })),
    ])
      .then(
        async ([
          checklistResponse,
          catalogResponse,
          queryResponse,
          findingResponse,
          activityResponse,
          reportResponse,
          documentResponse,
          approvalResponse,
          workflowResponse,
          evidenceResponse,
        ]) => {
          if (!active) return;
          const checklist =
            checklistResponse?.status && Array.isArray(checklistResponse.data)
              ? checklistResponse.data
              : [];
          setChecklistItems(checklist);
          setChecklistCatalog(
            catalogResponse?.status && Array.isArray(catalogResponse.data)
              ? catalogResponse.data
              : []
          );
          setQueries(
            queryResponse?.status && Array.isArray(queryResponse.data)
              ? queryResponse.data
              : []
          );
          setFindings(
            findingResponse?.status && Array.isArray(findingResponse.data)
              ? findingResponse.data
              : []
          );
          setActivities(
            activityResponse?.status && Array.isArray(activityResponse.data)
              ? activityResponse.data
              : []
          );
          setGeneratedReports(
            reportResponse?.status && Array.isArray(reportResponse.data)
              ? reportResponse.data
              : []
          );
          setReviewDocuments(
            documentResponse?.status && Array.isArray(documentResponse.data)
              ? documentResponse.data
              : []
          );
          setApprovalPermission(
            approvalResponse?.status && approvalResponse.data
              ? {
                  canApprove: Boolean(approvalResponse.data.canApprove),
                  approvers: approvalResponse.data.approvers || [],
                }
              : { canApprove: false, approvers: [] }
          );
          setWorkflowTasks(
            workflowResponse?.status && Array.isArray(workflowResponse.data)
              ? workflowResponse.data
              : []
          );
          setEvidenceCount(evidenceResponse?.status ? Number(evidenceResponse.data) || 0 : 0);
        }
      )
      .catch(() => {
        if (active) ErrorNotify("Unable to load review workspace data.");
      })
      .finally(() => {
        if (active) {
          showLoading(false);
          setRefreshingWorkspace(false);
        }
      });
    return () => {
      active = false;
    };
  }, [inspectionId, workspaceRefresh]);

  const engagement = review.engagementDetail?.name || "—";
  const client =
    review.engagementDetail?.client?.clientName ||
    review.engagementDetail?.client?.name ||
    review.engagementDetail?.clientName ||
    "—";
  const reviewType = review.inspectionType?.name || "Review";
  const reviewStatus =
    review.inspectionStatus?.name ||
    (Number(review.inspectionStatusId) === 2
      ? "In Progress"
      : Number(review.inspectionStatusId) === 3
      ? "Completed"
      : "New");
  const scopedItems = checklistItems.filter(item => (item.isInReviewScope ?? item.isInScope) !== false);
  const answeredCount = scopedItems.filter(isAnswered).length;
  const reviewIsCompleted = /^completed$/i.test(reviewStatus);
  const reviewActionLabel = reviewIsCompleted ? "View Checklists" : /progress/i.test(reviewStatus) ? "Resume Review" : "Start Review";
  const progress = scopedItems.length
    ? Math.round((answeredCount / scopedItems.length) * 100)
    : Math.max(0, Math.min(100, Number(review.percentage) || 0));
  const questionLookup = useMemo(
    () => new Map(checklistItems.map((item) => [String(item.id), item])),
    [checklistItems]
  );
  const currentUserId = currentUser?.id ?? currentUser?.userId;
  const activeFindingResponseTasks = useMemo(
    () =>
      workflowTasks.filter(
        (task) =>
          task.taskType === "FindingResponse" &&
          String(task.workflowStatus || "").toLowerCase() === "active" &&
          Number(task.assignedTo) === Number(currentUserId)
      ),
    [workflowTasks, currentUserId]
  );
  const findingResponseTaskByFindingId = useMemo(
    () =>
      new Map(
        activeFindingResponseTasks.map((task) => [Number(task.findingId), task])
      ),
    [activeFindingResponseTasks]
  );
  useEffect(() => {
    if (activeFindingResponseTasks.length) setActiveTab("findings");
  }, [activeFindingResponseTasks.length]);
  useEffect(() => {
    const taskId = String(focusedWorkflowTaskId || "");
    if (!taskId || focusedTaskRef.current === taskId) return;
    const task = workflowTasks.find((item) => String(item.id) === taskId);
    if (!task) return;
    focusedTaskRef.current = taskId;
    if (["QueryResponse", "QueryResponseReview", "QueryResponseApproval"].includes(task.taskType)) {
      const query = queries.find(
        (item) => Number(item.queryId) === Number(task.queryId)
      );
      if (!query) {
        ErrorNotify("The related query is no longer available.");
        return;
      }
      const question = checklistItems.find(
        (item) => Number(item.id) === Number(query.inspectionChecklistId)
      );
      setActiveTab("queries");
      setResponseDrawer({
        type: "query",
        task,
        item: {
          ...query,
          engagementName: engagement,
          questionCode: question
            ? `${question.referenceNo || "—"} - ${
                question.question || question.description || ""
              }`
            : "—",
          assigneeName: task.assignedUserName,
        },
      });
      setResponseText(query.finalResponse || "");
      setResponseAttachments([]);
    } else if (task.taskType === "FindingResponse") {
      const finding = findings.find(
        (item) => Number(item.id) === Number(task.findingId)
      );
      if (!finding) {
        ErrorNotify("The related finding is no longer available.");
        return;
      }
      const question = checklistItems.find(
        (item) => Number(item.id) === Number(finding.questionId)
      );
      setActiveTab("findings");
      setResponseDrawer({
        type: "finding",
        task,
        item: {
          ...finding,
          engagementName: engagement,
          checklistName: question?.checklistArea?.name || question?.areaName,
          questionCode: question
            ? `${question.referenceNo || "—"} - ${
                question.question || question.description || ""
              }`
            : "—",
          assigneeName: task.assignedUserName,
        },
      });
      setResponseText(finding.managementResponse || "");
      setResponseAttachments([]);
    }
  }, [
    focusedWorkflowTaskId,
    workflowTasks,
    queries,
    findings,
    checklistItems,
    engagement,
  ]);
  const sections = useMemo(() => {
    const grouped = new Map();
    checklistItems.forEach((item) => {
      const title = item.checklistArea?.name || item.areaName || "Unassigned";
      if (!grouped.has(title)) grouped.set(title, []);
      grouped.get(title).push(item);
    });
    return [...grouped.entries()].map(([title, items], index) => {
      const complete = items.filter(isAnswered).length;
      const questionIds = new Set(items.map((item) => String(item.id)));
      const firstCode =
        String(items[0]?.referenceNo || "").split(/[.\-]/)[0] ||
        String(index + 1);
      return {
        code: firstCode,
        title,
        total: items.length,
        complete,
        queries: queries.filter((query) =>
          questionIds.has(String(query.inspectionChecklistId))
        ).length,
      };
    });
  }, [checklistItems, queries]);
  const checklistCatalogById = useMemo(
    () => new Map(checklistCatalog.map((item) => [String(item.id), item])),
    [checklistCatalog]
  );
  const reviewChecklists = useMemo(() => {
    const grouped = new Map();
    checklistItems.forEach((item) => {
      const checklistId = String(item.checkListId || item.checklistId || "");
      if (!checklistId) return;
      const sectionName =
        item.checklistArea?.name || item.areaName || "Unassigned section";
      const groupId = checklistId;
      if (!grouped.has(groupId))
        grouped.set(groupId, { sectionName, items: [] });
      grouped.get(groupId).items.push(item);
    });
    return [...grouped.entries()]
      .map(([groupId, group]) => {
        const { sectionName, items } = group;
        const catalogs = [
          ...new Map(
            items
              .map((item) => {
                const checklistId = String(
                  item.checkListId || item.checklistId || ""
                );
                return [checklistId, checklistCatalogById.get(checklistId)];
              })
              .filter(([, catalog]) => catalog)
          ).values(),
        ];
        const scoped = items.filter(item => (item.isInReviewScope ?? item.isInScope) !== false);
        const complete = scoped.filter(isAnswered).length;
        const questionIds = new Set(items.map((item) => String(item.id)));
        const checklistNames = catalogs
          .map((catalog) => catalog.name)
          .filter(Boolean);
        const standards = [
          ...new Set(
            catalogs
              .map((catalog) => catalog.standardType?.name)
              .filter(Boolean)
          ),
        ];
        const workTypes = [
          ...new Set(
            catalogs
              .flatMap((catalog) => catalog.workTypes || [])
              .map((item) => item.workType?.name || item.name)
              .filter(Boolean)
          ),
        ];
        const industryTypes = [
          ...new Set(
            catalogs
              .flatMap((catalog) => catalog.industryTypes || [])
              .map((item) => item.industryType?.name || item.name)
              .filter(Boolean)
          ),
        ];
        return {
          id: groupId,
          name: checklistNames.join(", ") || items[0]?.checklistName || "Assigned checklist",
          sectionName: `${new Set(items.map((item) => item.areaId || item.checklistArea?.id || item.areaName)).size} sections`,
          standard: standards.join(", ") || "—",
          workTypes: workTypes.length ? workTypes.join(", ") : "All work types",
          industryTypes: industryTypes.length
            ? industryTypes.join(", ")
            : "All industries",
          questions: scoped.length,
          complete,
          progress: scoped.length
            ? Math.round((complete / scoped.length) * 100)
            : 0,
          queries: queries.filter((query) =>
            questionIds.has(String(query.inspectionChecklistId))
          ).length,
          status:
            scoped.length > 0 && complete === scoped.length
              ? "Completed"
              : complete
              ? "In Progress"
              : "Not Started",
        };
      })
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [checklistCatalogById, checklistItems, queries]);
  const assignedChecklistIds = useMemo(
    () =>
      new Set(
        checklistItems
          .map((item) => String(item.checkListId || item.checklistId || ""))
          .filter(Boolean)
      ),
    [checklistItems]
  );
  const availableChecklists = useMemo(
    () =>
      checklistCatalog.filter(
        (item) => !assignedChecklistIds.has(String(item.id))
      ),
    [assignedChecklistIds, checklistCatalog]
  );
  const recentQuestions = useMemo(
    () =>
      [...checklistItems]
        .sort(
          (left, right) =>
            new Date(right.updatedOn || right.createdOn || 0) -
            new Date(left.updatedOn || left.createdOn || 0)
        )
        .slice(0, 4),
    [checklistItems]
  );
  const team = useMemo(() => {
    const members = [
      [review.ownerUser?.fullName || review.reviewAssignment?.owner, "Owner"],
      [
        review.assignedUser?.fullName ||
          review.reviewAssignment?.primaryReviewer,
        "Reviewer",
      ],
      ...(review.reviewAssignment?.additionalReviewers || []).map((member) => [
        typeof member === "string" ? member : member.fullName,
        "Additional",
      ]),
    ].filter(([name]) => name);
    return [
      ...new Map(
        members.map(([name, role]) => [name, { name, role }])
      ).values(),
    ];
  }, [review]);
  const recentActivities = useMemo(
    () =>
      [...activities]
        .sort(
          (left, right) =>
            new Date(right.activityDate || right.createdOn || 0) -
            new Date(left.activityDate || left.createdOn || 0)
        )
        .slice(0, activeTab === "activity" ? activities.length : 4),
    [activities, activeTab]
  );
  const milestones = useMemo(() => {
    const items = [];
    if (review.dueDate)
      items.push({
        title: "Review due date",
        note: dueNote(review.dueDate),
        date: dateLabel(review.dueDate),
      });
    if (checklistItems.length && answeredCount < checklistItems.length)
      items.push({
        title: "Complete checklist responses",
        note: `${checklistItems.length - answeredCount} remaining`,
        date: review.dueDate ? dateLabel(review.dueDate) : "—",
      });
    if (queries.length)
      items.push({
        title: "Resolve open queries",
        note: `${queries.length} open`,
        date: review.dueDate ? dateLabel(review.dueDate) : "—",
      });
    return items;
  }, [answeredCount, checklistItems.length, queries.length, review.dueDate]);
  const tabs = [
    ["overview", "Overview"],
    ["sections", "Checklists", reviewChecklists.length],
    ["workflow", "Review Tasks", workflowTasks.length],
    ["queries", "Queries", queries.length],
    ["findings", "Findings", findings.length],
    ["observations", "Observations", observationCount],
    [
      "documents",
      "Documents",
      evidenceCount + generatedReports.length + reviewDocuments.length,
    ],
    ["team", "Team", team.length],
    ["activity", "Activity Timeline", activities.length],
  ];
  const isAwaitingApproval = /awaiting approval/i.test(reviewStatus);
  const canApproveReview = isAwaitingApproval && approvalPermission.canApprove;

  const startReview = async () => {
    if (reviewIsCompleted) {
      openChecklistAction?.(review);
      return;
    }
    if (activeFindingResponseTasks.length) {
      setActiveTab("findings");
      return;
    }
    if (!canStart) {
      setAssignmentMessageOpen(true);
      return;
    }
    if (!inspectionId) return;
    const emptyScope = reviewChecklists.find(item => item.questions === 0);
    if (emptyScope) {
      setActiveTab("sections"); setScopeChecklistId(Number(emptyScope.id));
      ErrorNotify("Select and save the question scope before starting this review.");
      return;
    }
    try {
      const response = await startInspectionReview(inspectionId);
      if (!response?.status)
        throw new Error(response?.message || "Unable to start the review.");
      openChecklistAction?.(review);
    } catch (error) {
      ErrorNotify(error.message || "Unable to start the review.");
    }
  };

  const refreshWorkspace = () => {
    if (!inspectionId || refreshingWorkspace) return;
    setRefreshingWorkspace(true);
    setWorkspaceRefresh((value) => value + 1);
  };

  const openAddChecklists = () => {
    setSelectedChecklistIds([]);
    setAddChecklistsOpen(true);
  };

  const toggleChecklistSelection = (checklistId) => {
    const id = Number(checklistId);
    setSelectedChecklistIds((items) =>
      items.includes(id) ? items.filter((item) => item !== id) : [...items, id]
    );
  };

  const saveAddedChecklists = async () => {
    if (!inspectionId || !selectedChecklistIds.length || addingChecklists)
      return;
    setAddingChecklists(true);
    try {
      const response = await addInspectionChecklists(
        inspectionId,
        selectedChecklistIds
      );
      if (!response?.status)
        throw new Error(
          response?.message || "Unable to add the selected checklists."
        );
      SuccessNotify(
        `${selectedChecklistIds.length} checklist${
          selectedChecklistIds.length === 1 ? "" : "s"
        } added to the review.`
      );
      setAddChecklistsOpen(false);
      setSelectedChecklistIds([]);
      setWorkspaceRefresh((value) => value + 1);
    } catch (error) {
      ErrorNotify(error?.message || "Unable to add the selected checklists.");
    } finally {
      setAddingChecklists(false);
    }
  };

  const approveReview = async () => {
    if (!inspectionId) return;
    try {
      const response = await approveInspection(inspectionId);
      if (!response?.status)
        throw new Error(response?.message || "Unable to approve this review.");
      SuccessNotify(
        `Review approved. ${
          response.data?.generatedReports || 0
        } final report(s) generated.`
      );
      closeAction?.();
    } catch (error) {
      ErrorNotify(error?.message || "Unable to approve this review.");
    }
  };

  const returnForChanges = async () => {
    if (
      !inspectionId ||
      !window.confirm("Return this review to the reviewer for changes?")
    )
      return;
    try {
      const response = await returnInspectionForChanges(inspectionId);
      if (!response?.status)
        throw new Error(
          response?.message || "Unable to return this review for changes."
        );
      SuccessNotify(
        `Review returned for changes: ${
          response.data?.status || "In Progress"
        }.`
      );
      closeAction?.();
    } catch (error) {
      ErrorNotify(
        error?.message || "Unable to return this review for changes."
      );
    }
  };

  const openGeneratedReport = async (report) => {
    setReportActionId(report.id);
    try {
      await previewInspectionDocument(inspectionId, report.id);
    } catch (error) {
      ErrorNotify(error?.message || "Unable to open the generated report.");
    } finally {
      setReportActionId(null);
    }
  };

  const downloadGeneratedReport = async (report) => {
    setReportActionId(report.id);
    try {
      await downloadInspectionDocument(
        inspectionId,
        report.id,
        report.fileName || report.name || "report.pdf"
      );
    } catch (error) {
      ErrorNotify(error?.message || "Unable to download the generated report.");
    } finally {
      setReportActionId(null);
    }
  };

  const openDocumentUpload = () => {
    setDocumentFiles([]);
    setDocumentCategory("WorkPaper");
    setDocumentDescription("");
    setDocumentUploadOpen(true);
  };

  const closeDocumentUpload = () => {
    if (uploadingDocuments) return;
    setDocumentUploadOpen(false);
    setDocumentFiles([]);
    setDocumentDescription("");
  };

  const openReviewDocument = async (document) => {
    setReportActionId(document.id);
    try {
      await previewInspectionDocument(inspectionId, document.id);
    } catch (error) {
      ErrorNotify(error?.message || "Unable to open the document.");
    } finally {
      setReportActionId(null);
    }
  };

  const downloadReviewDocument = async (document) => {
    setReportActionId(document.id);
    try {
      await downloadInspectionDocument(
        inspectionId,
        document.id,
        document.fileName || document.name || "document"
      );
    } catch (error) {
      ErrorNotify(error?.message || "Unable to download the document.");
    } finally {
      setReportActionId(null);
    }
  };

  const submitReviewDocuments = async (event) => {
    event.preventDefault();
    if (!documentFiles.length || uploadingDocuments) return;
    setUploadingDocuments(true);
    try {
      const formData = new FormData();
      formData.append("Category", documentCategory);
      formData.append("Description", documentDescription.trim());
      documentFiles.forEach((file) => formData.append("Documents", file));
      const response = await uploadInspectionReviewDocuments(
        inspectionId,
        formData
      );
      if (!response?.status)
        throw new Error(
          response?.message || "Unable to upload the selected documents."
        );
      SuccessNotify(
        `${documentFiles.length} document${
          documentFiles.length === 1 ? "" : "s"
        } uploaded successfully.`
      );
      setDocumentUploadOpen(false);
      setDocumentFiles([]);
      setDocumentDescription("");
      setWorkspaceRefresh((value) => value + 1);
    } catch (error) {
      ErrorNotify(error?.message || "Unable to upload the selected documents.");
    } finally {
      setUploadingDocuments(false);
    }
  };

  const openWorkflowTaskReassignment = (task) => {
    setWorkflowTaskToReassign(task);
    setWorkflowAssignee(String(task.assignedTo || ""));
  };

  const reassignWorkflowTask = async () => {
    if (!workflowTaskToReassign || !workflowAssignee) return;
    setReassigningWorkflowTask(true);
    try {
      const response = await reassignInspectionReviewWorkflowTask(
        inspectionId,
        workflowTaskToReassign.id,
        Number(workflowAssignee)
      );
      if (!response?.status)
        throw new Error(
          response?.message || "Unable to reassign the review task."
        );
      SuccessNotify("Review task reassigned successfully.");
      setWorkflowTaskToReassign(null);
      setWorkspaceRefresh((value) => value + 1);
    } catch (error) {
      ErrorNotify(error?.message || "Unable to reassign the review task.");
    } finally {
      setReassigningWorkflowTask(false);
    }
  };

  const openFindingResponse = (task) => {
    const finding = findings.find(
      (item) => Number(item.id) === Number(task.findingId)
    );
    if (!finding) {
      ErrorNotify("The related finding is no longer available.");
      return;
    }
    const question = questionLookup.get(String(finding.questionId));
    setResponseDrawer({
      type: "finding",
      task,
      item: {
        ...finding,
        engagementName: engagement,
        checklistName: question?.checklistArea?.name || question?.areaName,
        questionCode: question
          ? `${question.referenceNo || "—"} - ${
              question.question || question.description || ""
            }`
          : "—",
        assigneeName: task.assignedUserName,
      },
    });
    setResponseText(finding.managementResponse || "");
    setResponseAttachments([]);
  };

  const openQueryResponse = (task) => {
    const query = queries.find(
      (item) => Number(item.queryId) === Number(task.queryId)
    );
    if (!query) {
      ErrorNotify("The related query is no longer available.");
      return;
    }
    const question = questionLookup.get(String(query.inspectionChecklistId));
    setResponseDrawer({
      type: "query",
      task,
      item: {
        ...query,
        engagementName: engagement,
        questionCode: question
          ? `${question.referenceNo || "—"} - ${
              question.question || question.description || ""
            }`
          : "—",
        assigneeName: task.assignedUserName,
      },
    });
    setResponseText(query.finalResponse || "");
    setResponseAttachments([]);
  };

  const closeResponseDrawer = () => {
    if (savingResponse) return;
    setResponseDrawer(null);
    setResponseText("");
    setResponseAttachments([]);
  };

  const uploadResponseAttachment = async (file) => {
    const questionId = Number(responseDrawer?.item?.questionId);
    if (!file || !inspectionId || !questionId || uploadingResponseAttachment)
      return;
    setUploadingResponseAttachment(true);
    try {
      const formData = new FormData();
      formData.append("inspectionChecklistId", String(questionId));
      formData.append("document", file);
      const response = await uploadInspectionEvidence(inspectionId, formData);
      if (!response?.status)
        throw new Error(
          response?.message || "Unable to upload the response attachment."
        );
      setResponseAttachments((items) => [response.data, ...items]);
      SuccessNotify("Response attachment uploaded.");
    } catch (error) {
      ErrorNotify(
        error?.message || "Unable to upload the response attachment."
      );
    } finally {
      setUploadingResponseAttachment(false);
    }
  };

  const saveWorkflowResponse = async () => {
    if (!responseDrawer || !responseText.trim()) {
      ErrorNotify("Enter a response before submitting it.");
      return;
    }
    setSavingResponse(true);
    try {
      const response =
        responseDrawer.type === "query"
          ? await respondToInspectionChecklistQuery(
              inspectionId,
              responseDrawer.item.queryId,
              { response: responseText.trim() }
            )
          : await saveInspectionFindingManagementResponse(
              inspectionId,
              responseDrawer.item.id,
              {
                managementResponse: responseText.trim(),
                evidenceIds: responseAttachments
                  .map((file) => Number(file.id))
                  .filter(Boolean),
              }
            );
      if (!response?.status)
        throw new Error(
          response?.message ||
            `Unable to submit the ${responseDrawer.type} response.`
        );
      SuccessNotify(
        `${
          responseDrawer.type === "query" ? "Query" : "Finding"
        } response submitted.`
      );
      setResponseDrawer(null);
      setResponseText("");
      setResponseAttachments([]);
      setWorkspaceRefresh((value) => value + 1);
    } catch (error) {
      ErrorNotify(
        error?.message ||
          `Unable to submit the ${responseDrawer.type} response.`
      );
    } finally {
      setSavingResponse(false);
    }
  };

  const SectionList = () => (
    <section className="review-workspace__panel review-workspace__sections">
      <header className="review-workspace__panel-header">
        <h2>Checklists</h2>
        <button type="button" onClick={() => setExpanded((value) => !value)}>
          {expanded ? "Collapse All" : "Expand All"}
        </button>
      </header>
      <div className="review-workspace__section-list">
        {(expanded ? reviewChecklists : reviewChecklists.slice(0, 6)).length ? (
          (expanded ? reviewChecklists : reviewChecklists.slice(0, 6)).map((section) => {
            const percent = section.questions
              ? Math.round((section.complete / section.questions) * 100)
              : 0;
            const state =
              section.complete === section.questions
                ? "completed"
                : section.complete
                ? "progress"
                : "not-started";
            return (
              <button
                type="button"
                className={`review-workspace__section review-workspace__section--${state}`}
                key={section.id}
                onClick={startReview}
              >
                <i
                  className={`bx ${
                    state === "completed"
                      ? "bx-check-circle"
                      : state === "progress"
                      ? "bx-play-circle"
                      : "bx-circle"
                  }`}
                />
                <span className="review-workspace__section-copy">
                  <b>
                    {section.name}
                  </b>
                  <small>{section.questions} questions</small>
                </span>
                <span className="review-workspace__section-progress">
                  <i>
                    <em style={{ width: `${percent}%` }} />
                  </i>
                  <b>
                    {section.complete} / {section.questions}
                  </b>
                </span>
                <span className="review-workspace__section-query">
                  <i className="bx bx-message-rounded-dots" /> {section.queries}
                </span>
                <strong>
                  {state === "completed"
                    ? "Completed"
                    : state === "progress"
                    ? "In Progress"
                    : "Not Started"}
                </strong>
                <i className="bx bx-chevron-right" />
              </button>
            );
          })
        ) : (
          <div className="review-workspace__findings-empty">
            <b>No checklists are available</b>
          </div>
        )}
      </div>
    </section>
  );

  const QuestionList = () => (
    <section className="review-workspace__panel review-workspace__questions">
      <header className="review-workspace__panel-header">
        <h2>Recently Accessed Questions</h2>
        <button type="button" onClick={startReview}>
          View All
        </button>
      </header>
      {recentQuestions.length ? (
        recentQuestions.map((question) => {
          const answer = questionAnswer(question);
          return (
            <article key={question.id} className="review-workspace__question">
              <b>{question.referenceNo || "—"}</b>
              <div>
                <strong>
                  {question.question ||
                    question.description ||
                    "Untitled question"}
                </strong>
                <small>
                  {relativeTime(question.updatedOn || question.createdOn)}
                </small>
              </div>
              <span
                className={`review-workspace__answer review-workspace__answer--${answer.tone}`}
              >
                <i
                  className={`bx ${
                    answer.tone === "success"
                      ? "bx-check-circle"
                      : answer.tone === "danger"
                      ? "bx-x-circle"
                      : answer.tone === "warning"
                      ? "bx-message-rounded-dots"
                      : "bx-minus-circle"
                  }`}
                />{" "}
                {answer.label}
              </span>
            </article>
          );
        })
      ) : (
        <div className="review-workspace__findings-empty">
          <b>No questions are available</b>
        </div>
      )}
    </section>
  );

  const TeamPanel = () => (
    <section className={`${activeTab === "team" ? "review-workspace__register" : "review-workspace__panel"} review-workspace__team`}>
      <header className="review-workspace__panel-header">
        <div>
          <h2>Review Team</h2>
          {activeTab === "team" && <small>View assigned reviewers, participants, and their roles for this review.</small>}
        </div>
        {activeTab !== "team" && <button type="button" onClick={() => setActiveTab("team")}>
          Manage
        </button>}
      </header>
      {team.length ? (
        (activeTab === "team" ? team : team.slice(0, 4)).map((member) => (
          <article key={member.name}>
            <span>{initials(member.name)}</span>
            <div>
              <b>{member.name}</b>
              <small>{member.role}</small>
            </div>
            <em>{member.role}</em>
            <i className="bx bx-check-circle" />
          </article>
        ))
      ) : (
        <div className="review-workspace__findings-empty">
          <b>No review team members are assigned</b>
        </div>
      )}
      {activeTab !== "team" && team.length > 4 && (
        <button
          className="review-workspace__text-action"
          type="button"
          onClick={() => setActiveTab("team")}
        >
          View all team members ({team.length}){" "}
          <i className="bx bx-right-arrow-alt" />
        </button>
      )}
    </section>
  );

  const ActivityPanel = () => (
    <section className={`${activeTab === "activity" ? "review-workspace__register" : "review-workspace__panel"} review-workspace__activity`}>
      <header className="review-workspace__panel-header">
        <div>
          <h2>{activeTab === "activity" ? "Activity Timeline" : "Activity Feed"}</h2>
          {activeTab === "activity" && <small>Track review updates, responses, and actions in chronological order.</small>}
        </div>
        {activeTab !== "activity" && <button type="button" onClick={() => setActiveTab("activity")}>
          View All
        </button>}
      </header>
      {recentActivities.length ? (
        recentActivities.map((item) => (
          <article key={item.id}>
            <i
              className={`review-workspace__activity-dot review-workspace__activity-dot--${activityTone(
                item.activityNote
              )}`}
            />
            <div>
              <b>{item.activityNote || "Review activity"}</b>
              <small>
                {item.userProfile?.fullName
                  ? `by ${item.userProfile.fullName}`
                  : "—"}
              </small>
            </div>
            <time>{timeLabel(item.activityDate || item.createdOn)}</time>
          </article>
        ))
      ) : (
        <div className="review-workspace__findings-empty">
          <b>No activity has been recorded</b>
        </div>
      )}
    </section>
  );

  const QuickStart = () => (
    <section className="review-workspace__panel review-workspace__quick-start">
      <h2>Quick Start</h2>
      <div>
        <button
          type="button"
          onClick={startReview}
          disabled={!checklistItems.length}
        >
          <i className="bx bx-file" />
          <b>
            {reviewActionLabel}
          </b>
          <small>
            {answeredCount ? `${answeredCount} answered` : "Open checklist"}
          </small>
        </button>
        <button type="button" onClick={() => setActiveTab("queries")}>
          <i className="bx bx-message-rounded-dots" />
          <b>Open Queries</b>
          <small>
            {queries.length ? `${queries.length} open` : "No open queries"}
          </small>
        </button>
        <button
          type="button"
          onClick={startReview}
          disabled={!canStart || !checklistItems.length}
        >
          <i className="bx bx-paperclip" />
          <b>Upload Evidence</b>
          <small>
            {evidenceCount ? `${evidenceCount} attachments` : "Add attachments"}
          </small>
        </button>
      </div>
    </section>
  );
  const queryColumns = [
    {
      id: "queryId",
      header: "Query ID",
      accessorFn: (query) => query.id || query.queryId || "—",
      size: 120,
      flex: 0.7,
    },
    {
      id: "question",
      header: "Question",
      accessorFn: (query) => {
        const question = questionLookup.get(
          String(query.inspectionChecklistId)
        );
        return question
          ? `${question.referenceNo || "—"} ${
              question.question || question.description || ""
            }`
          : "—";
      },
      size: 260,
      flex: 1.5,
    },
    {
      id: "raisedBy",
      header: "Raised By",
      accessorFn: (query) => query.raisedBy || query.assigneeName || "—",
      size: 160,
      flex: 0.9,
    },
    {
      id: "raisedDate",
      header: "Raised Date",
      accessorFn: (query) => dateLabel(query.createdOn),
      size: 135,
      flex: 0.75,
    },
    {
      id: "status",
      header: "Status",
      accessorFn: (query) => query.status || "Open",
      size: 115,
      flex: 0.65,
      cell: ({ getValue }) => (
        <div className="review-workspace__finding-badge-cell">
          <span className="review-workspace__finding-status">{getValue()}</span>
        </div>
      ),
    },
    {
      id: "actions",
      header: "Action",
      accessorFn: () => "",
      size: 165,
      flex: 0,
      enableSorting: false,
      enableColumnFilter: false,
      enableExport: false,
      cell: ({ row }) => {
        const query = row.original;
        const responseTask = workflowTasks.find(
          (item) =>
            item.taskType === "QueryResponse" &&
            Number(item.queryId) === Number(query.queryId) &&
            String(item.workflowStatus || "").toLowerCase() === "active" &&
            Number(item.assignedTo) === Number(currentUserId)
        );
        return responseTask ? (
          <button
            className="review-workspace__finding-action"
            type="button"
            onClick={() => openQueryResponse(responseTask)}
          >
            Respond to query
          </button>
        ) : (
          <span>—</span>
        );
      },
    },
  ];
  const registerGridSx = {
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: "#ffffff !important",
      borderBottom: "1px solid #e4e8f0",
    },
    "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader--sortable": {
      backgroundColor: "#ffffff !important",
      color: "#52627f",
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      color: "#52627f",
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: ".2px",
      textTransform: "none",
    },
    "& .MuiDataGrid-columnSeparator": { color: "#e7ebf2" },
    "& .MuiDataGrid-cell": {
      alignItems: "center",
      borderBottomColor: "#edf0f4",
      display: "flex",
      fontSize: 13,
    },
    "& .MuiDataGrid-cell .review-workspace__finding-badge-cell": {
      alignSelf: "center",
      height: "22px !important",
      maxHeight: "22px",
      minHeight: "22px !important",
    },
    "& .MuiDataGrid-cell .review-workspace__finding-status": {
      alignSelf: "center",
      height: "22px !important",
      maxHeight: "22px",
      minHeight: "22px !important",
    },
    "& .MuiDataGrid-cell .review-workspace__finding-action, & .MuiDataGrid-cell .review-workspace__task-reassign":
      {
        alignSelf: "center",
        height: "30px !important",
        maxHeight: "30px",
        minHeight: "30px !important",
      },
    '& .MuiDataGrid-columnHeader[data-field="actions"] .MuiDataGrid-columnHeaderTitleContainer':
      { justifyContent: "center" },
    '& .MuiDataGrid-cell[data-field="actions"]': {
      justifyContent: "center",
      padding: "0 12px",
    },
    "& .MuiDataGrid-row:hover": { backgroundColor: "#f8faff" },
  };
  const scopeQuestions = (id) => checklistItems.filter(item => Number(item.checkListId || item.checklistId) === Number(id));
  const canDefineScope = (id) => {
    const questions = scopeQuestions(id);
    return canContributeToReview(currentUser) && !reviewIsCompleted && !isAwaitingApproval &&
      questions.length > 0 && questions.every(item => Number(item.primaryReviewerId ?? review.assignedTo ?? review.assignedUser?.id) === Number(currentUserId)) &&
      !workflowTasks.some(task => Number(task.checklistId) === Number(id) && task.workflowStatus === "Completed");
  };
  const defineScope = (id) => { setActiveTab("sections"); setScopeChecklistId(Number(id)); };
  const scopeChecklist = reviewChecklists.find(item => Number(item.id) === Number(scopeChecklistId));

  const checklistColumns = [
    {
      id: "name",
      header: "Checklist",
      accessorFn: (item) => `${item.name} ${item.sectionName}`,
      size: 310,
      flex: 1.55,
      cell: ({ row }) => (
        <div
          className="review-workspace__checklist-name"
          title={`${row.original.name} — ${row.original.sectionName}`}
        >
          <b>{row.original.name}</b>
          <small>
            {row.original.sectionName} · {row.original.standard}
          </small>
        </div>
      ),
    },
    {
      id: "workTypes",
      header: "Work Type",
      accessorFn: (item) => item.workTypes,
      size: 210,
      flex: 1.1,
      cell: ({ getValue }) => (
        <span
          className="review-workspace__checklist-metadata"
          title={getValue()}
        >
          {getValue()}
        </span>
      ),
    },
    {
      id: "industryTypes",
      header: "Industry Type",
      accessorFn: (item) => item.industryTypes,
      size: 230,
      flex: 1.2,
      cell: ({ getValue }) => (
        <span
          className="review-workspace__checklist-metadata"
          title={getValue()}
        >
          {getValue()}
        </span>
      ),
    },
    {
      id: "questions",
      header: "Questions",
      accessorFn: (item) => item.questions,
      size: 100,
      flex: 0.55,
    },
    {
      id: "progress",
      header: "Progress",
      accessorFn: (item) => item.progress,
      size: 175,
      flex: 0.85,
      cell: ({ row }) => (
        <div className="review-workspace__checklist-progress">
          <span>
            <i style={{ width: `${row.original.progress}%` }} />
          </span>
          <b>
            {row.original.complete} / {row.original.questions}
          </b>
        </div>
      ),
    },
    {
      id: "queries",
      header: "Queries",
      accessorFn: (item) => item.queries,
      size: 90,
      flex: 0.5,
      cell: ({ getValue }) => (
        <span className="review-workspace__checklist-query">
          <i className="bx bx-message-rounded-dots" /> {getValue()}
        </span>
      ),
    },
    {
      id: "status",
      header: "Status",
      accessorFn: (item) => item.status,
      size: 125,
      flex: 0.65,
      cell: ({ getValue }) => (
        <div className="review-workspace__finding-badge-cell">
          <span
            className={`review-workspace__checklist-status status--${String(
              getValue()
            )
              .toLowerCase()
              .replaceAll(" ", "-")}`}
          >
            {getValue()}
          </span>
        </div>
      ),
    },
    {
      id: "actions",
      header: "Action",
      accessorFn: () => "",
      size: 220,
      flex: 0,
      enableSorting: false,
      enableColumnFilter: false,
      enableExport: false,
      cell: ({ row }) => (
        <div className="review-workspace__scope-actions">
        {canDefineScope(row.original.id) && <button type="button" className="review-workspace__checklist-open" onClick={() => defineScope(row.original.id)}>Define Scope</button>}
        <button
          className="review-workspace__checklist-open"
          type="button"
          onClick={startReview}
        >
          Open <i className="bx bx-right-arrow-alt" />
        </button>
        </div>
      ),
    },
  ];

  const visibleChecklists = reviewChecklists.filter((item) =>
    (!checklistStatus || item.status === checklistStatus) &&
    checklistColumns.filter((column) => column.id !== "actions")
      .map((column) => column.accessorFn(item)).join(" ")
      .toLowerCase().includes(checklistSearch.trim().toLowerCase())
  );
  const ChecklistsGrid = () => (
    <section className="review-workspace__register review-workspace__checklists-grid">
      <header className="review-workspace__panel-header">
        <div>
          <h2>Checklists</h2>
          <small>
            All assigned checklists and their current completion status.
          </small>
        </div>
        <button
          className="review-workspace__add-checklist"
          type="button"
          onClick={openAddChecklists}
          hidden={Boolean(review.inspectionCycleEngagementId)}
        >
          <i className="bx bx-plus" /> Add Checklists
        </button>
      </header>
      
      {scopeChecklist && canDefineScope(scopeChecklist.id) && <div className="checklist-selection review-workspace__scope-editor">
        <h3>{scopeChecklist.name}</h3>
        <ChecklistQuestionScope key={`${inspectionId}:${scopeChecklist.id}`} reviewId={inspectionId}
          checklist={{ id: Number(scopeChecklist.id), questions: scopeQuestions(scopeChecklist.id).map(item => ({ ...item, isInScope: item.isInReviewScope ?? item.isInScope })) }}
          lockedQuestionIds={[...queries, ...findings].map(item => item.inspectionChecklistId).filter(Boolean)}
          onSaved={() => { setScopeChecklistId(null); refreshWorkspace(); SuccessNotify("Question scope saved."); }} />
      </div>}
      {scopeChecklistId && checklistItems.length > 0 && !canDefineScope(scopeChecklistId) && <p role="status">Only the assigned inspection team member can define scope before checklist submission.</p>}
      {reviewChecklists.length ? (
        <CustomDataGrid
 filterContent={<div className="review-workspace__finding-filters review-workspace__checklist-filters">
        {quickFilters("Statuses", checklistStatus, setChecklistStatus, reviewChecklists.map((item) => item.status))}
        <label>
          <i className="bx bx-search" />
          <input aria-label="Search checklists" placeholder="Search checklists" value={checklistSearch} onChange={(event) => setChecklistSearch(event.target.value)} />
        </label>
        <button type="button" onClick={() => { setChecklistSearch(""); setChecklistStatus(""); }}>Clear</button>
      </div>}

          data={visibleChecklists}
          columns={checklistColumns}
          fileName="review-checklists"
          recordLabel="checklists"
          showToolbar={false}
          initialPageSize={25}
          rowHeight={64}
          columnHeaderHeight={48}
          slots={{ noRowsOverlay: () => <div className="review-workspace__findings-empty">No checklists match these filters.</div> }}
          gridSx={registerGridSx}
        />
      ) : (
        <div className="review-workspace__findings-empty">
          <i className="bx bx-list-check" />
          <b>No checklists are assigned</b>
          <button type="button" hidden={Boolean(review.inspectionCycleEngagementId)} onClick={openAddChecklists}>
            Add Checklists
          </button>
        </div>
      )}
    </section>
  );

  const ResponseDrawer = () => (
    <WorkflowResponseDrawer
      inspectionId={inspectionId}
      onReviewPosted={refreshWorkspace}
      response={responseDrawer}
      value={responseText}
      onChange={setResponseText}
      onClose={closeResponseDrawer}
      onSubmit={saveWorkflowResponse}
      attachments={responseAttachments}
      onUpload={
        responseDrawer?.type === "finding" ? uploadResponseAttachment : null
      }
      uploading={uploadingResponseAttachment}
      saving={savingResponse}
    />
  );

  const visibleQueries = queries.filter((query) =>
    (!queryStatus || (query.status || "Open") === queryStatus) &&
    queryColumns.filter((column) => column.id !== "actions")
      .map((column) => column.accessorFn(query)).join(" ")
      .toLowerCase().includes(querySearch.trim().toLowerCase())
  );
  const quickFilters = (label, value, onChange, values) => (
    <label>
      <span>Status</span>
      <select aria-label={`Filter by ${label.toLowerCase()}`} value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">All statuses</option>
        {[...new Set(values.filter(Boolean))].sort().map((option) => (
          <option key={option} value={option}>{option.replace(/([a-z])([A-Z])/g, "$1 $2")}</option>
        ))}
      </select>
    </label>
  );
  const QueriesTable = () => (
    <>
      <section className="review-workspace__register review-workspace__query-table">
        <header className="review-workspace__panel-header">
          <div>
            <h2>Queries</h2>
            <small>Track open queries, responses, and resolution for this review.</small>
          </div>
          <button type="button" onClick={startReview}>
            View All
          </button>
        </header>
        
        {queries.length ? (
          <CustomDataGrid
 filterContent={<div className="review-workspace__finding-filters">
          {quickFilters("Statuses", queryStatus, setQueryStatus, queries.map((query) => query.status || "Open"))}
          <label>
            <i className="bx bx-search" />
            <input aria-label="Search queries" placeholder="Search queries" value={querySearch} onChange={(event) => setQuerySearch(event.target.value)} />
          </label>
          <button type="button" onClick={() => { setQuerySearch(""); setQueryStatus(""); }}>Clear</button>
        </div>}

            data={visibleQueries}
            columns={queryColumns}
            fileName="queries"
            recordLabel="queries"
            showToolbar={false}
            initialPageSize={10}
            rowHeight={52}
            columnHeaderHeight={44}
            slots={{ noRowsOverlay: () => <div className="review-workspace__findings-empty">No queries match these filters.</div> }}
            gridSx={registerGridSx}
          />
        ) : (
          <div className="review-workspace__findings-empty">
            <b>No open queries.</b>
          </div>
        )}
      </section>
      <ResponseDrawer />
    </>
  );

  const Milestones = () => (
    <section className="review-workspace__panel review-workspace__milestones">
      <h2>Upcoming Milestones</h2>
      {milestones.length ? (
        milestones.map((milestone) => (
          <article key={milestone.title}>
            <i className="bx bx-check-circle" />
            <div>
              <b>{milestone.title}</b>
              <small>{milestone.note}</small>
            </div>
            <time>
              <i className="bx bx-calendar" /> {milestone.date}
            </time>
          </article>
        ))
      ) : (
        <div className="review-workspace__findings-empty">
          <b>No upcoming milestones</b>
        </div>
      )}
    </section>
  );

  const UploadDocumentsModal = () => (
    <Modal
      centered
      size="lg"
      isOpen={documentUploadOpen}
      toggle={closeDocumentUpload}
    >
      <ModalBody className="review-workspace__document-upload-modal">
        <form onSubmit={submitReviewDocuments}>
          <header>
            <div>
              <h2>Upload Work Papers / Documents</h2>
              <p>
                Add review-level files separately from system-generated reports.
              </p>
            </div>
            <button
              type="button"
              onClick={closeDocumentUpload}
              disabled={uploadingDocuments}
              aria-label="Close"
            >
              <i className="bx bx-x" />
            </button>
          </header>
          <div className="review-workspace__document-upload-fields">
            <label>
              <span>
                Document Type <em>*</em>
              </span>
              <select
                value={documentCategory}
                onChange={(event) => setDocumentCategory(event.target.value)}
                disabled={uploadingDocuments}
              >
                <option value="WorkPaper">Work Paper</option>
                <option value="Document">Document</option>
              </select>
            </label>
            <label>
              <span>Description</span>
              <textarea
                rows="3"
                maxLength="1000"
                value={documentDescription}
                onChange={(event) => setDocumentDescription(event.target.value)}
                disabled={uploadingDocuments}
                placeholder="Add an optional description for these files"
              />
              <small>{documentDescription.length}/1000</small>
            </label>
            <label className="review-workspace__document-dropzone">
              <input
                ref={documentUploadRef}
                type="file"
                multiple
                hidden
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt,.png,.jpg,.jpeg"
                onChange={(event) =>
                  setDocumentFiles(Array.from(event.target.files || []))
                }
                disabled={uploadingDocuments}
              />
              <i className="bx bx-cloud-upload" />
              <b>
                {documentFiles.length
                  ? "Choose Different Files"
                  : "Browse Files"}
              </b>
              <small>PDF, Office files, text, CSV, PNG or JPG</small>
            </label>
            {documentFiles.length > 0 && (
              <div className="review-workspace__pending-documents">
                {documentFiles.map((file, index) => (
                  <article key={`${file.name}-${file.size}-${index}`}>
                    <i className={`bx ${documentIcon(file.name)}`} />
                    <span>
                      <b>{file.name}</b>
                      <small>
                        {Math.max(1, Math.round(file.size / 1024))} KB
                      </small>
                    </span>
                    <button
                      type="button"
                      disabled={uploadingDocuments}
                      onClick={() =>
                        setDocumentFiles((files) =>
                          files.filter((_, fileIndex) => fileIndex !== index)
                        )
                      }
                      aria-label={`Remove ${file.name}`}
                    >
                      <i className="bx bx-x" />
                    </button>
                  </article>
                ))}
              </div>
            )}
          </div>
          <footer>
            <span>
              {documentFiles.length} file{documentFiles.length === 1 ? "" : "s"}{" "}
              selected
            </span>
            <button
              type="button"
              onClick={closeDocumentUpload}
              disabled={uploadingDocuments}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="is-primary"
              disabled={uploadingDocuments || !documentFiles.length}
            >
              <i
                className={`bx ${
                  uploadingDocuments
                    ? "bx-loader-alt bx-spin"
                    : "bx-cloud-upload"
                }`}
              />{" "}
              {uploadingDocuments ? "Uploading..." : "Upload"}
            </button>
          </footer>
        </form>
      </ModalBody>
    </Modal>
  );

  const DocumentsPanel = () => (
    <section className="review-workspace__documents">
      <section className="review-workspace__register review-workspace__review-documents">
        <header className="review-workspace__panel-header">
          <div>
            <h2>Documents</h2>
            <small>
              Files uploaded by review participants, separate from generated
              reports.
            </small>
          </div>
          <div className="review-workspace__document-header-actions">
            <b>{reviewDocuments.length}</b>
            <button type="button" onClick={openDocumentUpload}>
              <i className="bx bx-cloud-upload" /> Upload Documents
            </button>
          </div>
        </header>
        {reviewDocuments.length ? (
          <div className="review-workspace__report-list review-workspace__document-list">
            {reviewDocuments.map((document) => {
              const fileName = document.fileName || document.name || "Document";
              return (
                <article key={document.id}>
                  <i className={`bx ${documentIcon(fileName)}`} />
                  <div>
                    <b>{fileName}</b>
                    <small>
                      {document.category || "Document"} · Uploaded by{" "}
                      {document.uploadedBy || "Review participant"} on{" "}
                      {dateLabel(document.createdOn || document.updatedOn)}
                      {document.description ? ` · ${document.description}` : ""}
                    </small>
                  </div>
                  <span>
                    {document.sizeBytes
                      ? `${Math.max(
                          1,
                          Math.round(Number(document.sizeBytes) / 1024)
                        )} KB`
                      : "File"}
                  </span>
                  <button
                    type="button"
                    onClick={() => openReviewDocument(document)}
                    disabled={reportActionId === document.id}
                  >
                    <i
                      className={`bx ${
                        reportActionId === document.id
                          ? "bx-loader-alt bx-spin"
                          : "bx-show"
                      }`}
                    />{" "}
                    View
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadReviewDocument(document)}
                    disabled={reportActionId === document.id}
                  >
                    <i className="bx bx-download" /> Download
                  </button>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="review-workspace__reports-empty">
            <i className="bx bx-folder-open" />
            <b>No work papers or documents uploaded</b>
            <p>
              Upload review-level files that are not tied to an individual
              checklist question.
            </p>
            <button type="button" onClick={openDocumentUpload}>
              <i className="bx bx-cloud-upload" /> Upload Documents
            </button>
          </div>
        )}
      </section>
      <section className="review-workspace__register review-workspace__report-register">
        <header className="review-workspace__panel-header">
          <div>
            <h2>Generated Reports</h2>
            <small>
              PDF reports created from the scoped review checklists.
            </small>
          </div>
          <b>{generatedReports.length}</b>
        </header>
        {generatedReports.length ? (
          <div className="review-workspace__report-list">
            {generatedReports.map((report) => (
              <article key={report.id}>
                <i className="bx bx-file-pdf" />
                <div>
                  <b>
                    {report.fileName || report.name || "Generated report.pdf"}
                  </b>
                  <small>
                    {/final-sign-off/i.test(report.description || "")
                      ? "Final sign-off report"
                      : "Review-completion report"}{" "}
                    · Generated{" "}
                    {dateLabel(report.createdOn || report.updatedOn)}
                  </small>
                </div>
                <span>
                  {report.sizeBytes
                    ? `${Math.max(
                        1,
                        Math.round(Number(report.sizeBytes) / 1024)
                      )} KB`
                    : "PDF"}
                </span>
                <button
                  type="button"
                  onClick={() => openGeneratedReport(report)}
                  disabled={reportActionId === report.id}
                >
                  <i
                    className={`bx ${
                      reportActionId === report.id
                        ? "bx-loader-alt bx-spin"
                        : "bx-show"
                    }`}
                  />{" "}
                  View
                </button>
                <button
                  type="button"
                  onClick={() => downloadGeneratedReport(report)}
                  disabled={reportActionId === report.id}
                >
                  <i className="bx bx-download" /> Download
                </button>
              </article>
            ))}
          </div>
        ) : (
          <div className="review-workspace__reports-empty">
            <i className="bx bx-file-pdf" />
            <b>No report has been generated yet</b>
            <p>
              A PDF will appear here after the review is submitted for approval.
            </p>
          </div>
        )}
      </section>
      <section className="review-workspace__panel review-workspace__evidence-summary">
        <i className="bx bx-paperclip" />
        <div>
          <h2>Checklist Evidence</h2>
          <p>
            {evidenceCount
              ? `${evidenceCount} file${
                  evidenceCount === 1 ? " is" : "s are"
                } attached to checklist questions in this review.`
              : "No supporting evidence has been attached to checklist questions."}
          </p>
        </div>
        <button
          type="button"
          onClick={startReview}
          disabled={!checklistItems.length}
        >
          Open Review <i className="bx bx-right-arrow-alt" />
        </button>
      </section>
      <UploadDocumentsModal />
    </section>
  );

  const workflowTaskStage = (task) =>
    task.taskType === "FinalSignOff"
      ? `Final sign-off · ${task.assignedRole || "Approver"}`
      : task.taskType === "FindingResponse"
      ? `Finding response · ${task.assignedRole || "Assignee"}`
      : ["QueryResponse", "QueryResponseReview", "QueryResponseApproval"].includes(task.taskType)
      ? `Query response · ${task.assignedRole || "Assignee"}`
      : `Checklist ${task.checklistId || "—"} · ${
          task.assignedRole || "Reviewer"
        }`;
  const workflowTaskColumns = [
    {
      id: "task",
      header: "Task",
      accessorFn: (task) => task.taskName || "—",
      size: 280,
      flex: 1.45,
      cell: ({ getValue }) => (
        <b className="review-workspace__task-name">{getValue()}</b>
      ),
    },
    {
      id: "stage",
      header: "Checklist / Stage",
      accessorFn: workflowTaskStage,
      size: 280,
      flex: 1.4,
    },
    {
      id: "assignedTo",
      header: "Assigned To",
      accessorFn: (task) => task.assignedUserName || "Unassigned",
      size: 190,
      flex: 1,
    },
    {
      id: "status",
      header: "Status",
      accessorFn: (task) => task.workflowStatus || "—",
      size: 120,
      flex: 0.65,
      cell: ({ getValue }) => (
        <div className="review-workspace__finding-badge-cell">
          <span className="review-workspace__finding-status">{getValue()}</span>
        </div>
      ),
    },
    ...(canManage
      ? [
          {
            id: "actions",
            header: "Action",
            accessorFn: () => "",
            size: 135,
            flex: 0,
            enableSorting: false,
            enableColumnFilter: false,
            enableExport: false,
            cell: ({ row }) =>
              String(row.original.workflowStatus || "").toLowerCase() ===
              "active" ? (
                <button
                  className="review-workspace__task-reassign"
                  type="button"
                  onClick={() => openWorkflowTaskReassignment(row.original)}
                >
                  Reassign
                </button>
              ) : (
                <span>—</span>
              ),
          },
        ]
      : []),
  ];

  const visibleWorkflowTasks = workflowTasks.filter((task) =>
    (!taskStatus || (task.workflowStatus || "—") === taskStatus) &&
    `${task.taskName || ""} ${workflowTaskStage(task)} ${task.assignedUserName || "Unassigned"} ${task.workflowStatus || ""}`
      .toLowerCase().includes(taskSearch.trim().toLowerCase())
  );
  const WorkflowTasksPanel = () => (
    <div className="review-workspace__register review-workspace__workflow-tasks">
      <header className="review-workspace__panel-header">
        <div>
          <h2>Review Tasks</h2>
          <small>Track checklist reviews, query and finding responses, and sign-offs.</small>
        </div>
      </header>
      
      {workflowTasks.length ? (
        <CustomDataGrid
 filterContent={<div className="review-workspace__finding-filters">
        {quickFilters("Statuses", taskStatus, setTaskStatus, workflowTasks.map((task) => task.workflowStatus || "—"))}
        <label>
          <i className="bx bx-search" />
          <input aria-label="Search review tasks" placeholder="Search review tasks" value={taskSearch} onChange={(event) => setTaskSearch(event.target.value)} />
        </label>
        <button type="button" onClick={() => { setTaskSearch(""); setTaskStatus(""); }}>Clear</button>
      </div>}

          data={visibleWorkflowTasks}
          columns={workflowTaskColumns}
          fileName="review-workflow-tasks"
          recordLabel="review workflow tasks"
          showToolbar={false}
          initialPageSize={10}
          rowHeight={52}
          columnHeaderHeight={44}
          slots={{ noRowsOverlay: () => <div className="review-workspace__findings-empty">No review tasks match these filters.</div> }}
          gridSx={registerGridSx}
        />
      ) : (
        <div className="review-workspace__findings-empty">
          <b>No review workflow tasks are configured for this review.</b>
        </div>
      )}
    </div>
  );
  const findingColumns = [
    {
      id: "finding",
      header: "Finding",
      accessorFn: (finding) => finding.findingNumber || "—",
      size: 220,
      flex: 1.35,
      cell: ({ row }) => (
        <div className="review-workspace__finding-cell">
          <b>{row.original.findingNumber || "—"}</b>
          <span>{row.original.title || "Untitled finding"}</span>
        </div>
      ),
    },
    {
      id: "description",
      header: "Description",
      accessorFn: (finding) => finding.description || "—",
      size: 280,
      flex: 1.65,
      cell: ({ getValue }) => (
        <span
          className="review-workspace__finding-description"
          title={getValue()}
        >
          {getValue()}
        </span>
      ),
    },
    {
      id: "source",
      header: "Source",
      accessorFn: (finding) =>
        finding.questionId
          ? questionLookup.get(String(finding.questionId))?.referenceNo ||
            `Question ${finding.questionId}`
          : "General engagement",
      size: 130,
      flex: 0.8,
    },
    {
      id: "category",
      header: "Category",
      accessorFn: (finding) => finding.category || "—",
      size: 150,
      flex: 0.9,
    },
    {
      id: "severity",
      header: "Severity",
      accessorFn: (finding) => finding.severity || "—",
      size: 105,
      flex: 0.65,
      cell: ({ getValue }) => (
        <div className="review-workspace__finding-badge-cell">
          <em
            className={`review-workspace__priority review-workspace__priority--${String(
              getValue() || "low"
            ).toLowerCase()}`}
          >
            {getValue()}
          </em>
        </div>
      ),
    },
    {
      id: "responseOwner",
      header: "Response Owner",
      accessorFn: (finding) =>
        findingResponseTaskByFindingId.get(Number(finding.id))
          ?.assignedUserName ||
        (finding.managementResponseRequired
          ? "Assignment pending"
          : "Not required"),
      size: 185,
      flex: 1,
    },
    {
      id: "responseDue",
      header: "Response Due",
      accessorFn: (finding) =>
        finding.responseDueDate ? dateLabel(finding.responseDueDate) : "—",
      size: 130,
      flex: 0.75,
    },
    {
      id: "status",
      header: "Status",
      accessorFn: (finding) => finding.findingStatus || "—",
      size: 120,
      flex: 0.7,
      cell: ({ getValue }) => (
        <div className="review-workspace__finding-badge-cell">
          <span className="review-workspace__finding-status">{getValue()}</span>
        </div>
      ),
    },
    {
      id: "raisedDate",
      header: "Raised Date",
      accessorFn: (finding) => dateLabel(finding.createdOn),
      size: 135,
      flex: 0.75,
    },
    {
      id: "actions",
      header: "Action",
      accessorFn: () => "",
      size: 165,
      flex: 0,
      enableSorting: false,
      enableColumnFilter: false,
      enableExport: false,
      cell: ({ row }) => {
        const responseTask = findingResponseTaskByFindingId.get(
          Number(row.original.id)
        );
        return responseTask ? (
          <button
            className="review-workspace__finding-action"
            type="button"
            onClick={() => openFindingResponse(responseTask)}
          >
            Respond to finding
          </button>
        ) : (
          <span>—</span>
        );
      },
    },
  ];

  const TabContent = () => {
    if (activeTab === "overview")
      return (
        <>
          <div className="review-workspace__overview-grid">
            <SectionList />
            <div className="review-workspace__middle">
              <QuestionList />
              <QuickStart />
            </div>
            <aside>
              <TeamPanel />
              <ActivityPanel />
            </aside>
          </div>
          <div className="review-workspace__bottom-grid">
            {QueriesTable()}
            <Milestones />
          </div>
        </>
      );
    if (activeTab === "sections") return ChecklistsGrid();
    if (activeTab === "workflow") return WorkflowTasksPanel();
    if (activeTab === "queries") return QueriesTable();
    if (activeTab === "observations") return <><ObservationsPanel key={observationRefresh} inspectionId={inspectionId} canEdit={canManage || canStart} onCount={setObservationCount} onConvert={setConvertingObservation} />{convertingObservation && <ObservationFindingConversion key={convertingObservation.id} inspectionId={inspectionId} observation={convertingObservation} onClose={() => setConvertingObservation(null)} onSaved={(finding) => { setFindings((current) => [finding, ...current]); setConvertingObservation(null); setObservationRefresh((value) => value + 1); }} />}</>;
    if (activeTab === "findings") {
      const visibleFindings = findings.filter((finding) =>
        (!findingStatus || (finding.findingStatus || "Open") === findingStatus) &&
        `${finding.findingNumber} ${finding.title} ${finding.category} ${finding.severity} ${finding.findingStatus}`
          .toLowerCase()
          .includes(findingSearch.trim().toLowerCase())
      );
      return (
        <>
          <section className="review-workspace__register review-workspace__findings-register">
            <header className="review-workspace__panel-header">
              <div>
                <h2>Findings</h2>
                <small>Master register for this engagement review</small>
              </div>
            </header>
            
            {findings.length ? (
              <CustomDataGrid
 filterContent={<div className="review-workspace__finding-filters">
              {quickFilters("Statuses", findingStatus, setFindingStatus, findings.map((finding) => finding.findingStatus || "Open"))}
              <label>
                <i className="bx bx-search" />
                <input
                  value={findingSearch}
                  onChange={(event) => setFindingSearch(event.target.value)}
                  placeholder="Search findings"
                  aria-label="Search findings"
                />
              </label>
              <button type="button" onClick={() => { setFindingSearch(""); setFindingStatus(""); }}>Clear</button>
            </div>}

                data={visibleFindings}
                columns={findingColumns}
                fileName="findings"
                recordLabel="findings"
                showToolbar={false}
                initialPageSize={10}
                rowHeight={58}
                columnHeaderHeight={48}
                slots={{ noRowsOverlay: () => <div className="review-workspace__findings-empty">No findings match these filters.</div> }}
                gridSx={{
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#ffffff !important",
                    borderBottom: "1px solid #e4e8f0",
                  },
                  "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader--sortable":
                    { backgroundColor: "#ffffff !important", color: "#52627f" },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    color: "#52627f",
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: ".2px",
                  },
                  "& .MuiDataGrid-columnSeparator": { color: "#e7ebf2" },
                  "& .MuiDataGrid-cell": {
                    alignItems: "center",
                    borderBottomColor: "#edf0f5",
                    color: "#273957",
                    display: "flex",
                    fontSize: 13,
                  },
                  '& .MuiDataGrid-columnHeader[data-field="actions"] .MuiDataGrid-columnHeaderTitleContainer':
                    { justifyContent: "center" },
                  '& .MuiDataGrid-cell[data-field="actions"]': {
                    justifyContent: "center",
                    padding: "0 12px",
                  },
                  "& .MuiDataGrid-row:hover": { backgroundColor: "#f8faff" },
                }}
              />
            ) : (
              <div className="review-workspace__findings-empty">
                <i className="bx bx-flag" />
                <b>No findings have been recorded</b>
                <p>Raise a finding from a checklist question.</p>
              </div>
            )}
          </section>
          <ResponseDrawer />
        </>
      );
    }
    if (activeTab === "documents") return <DocumentsPanel />;
    if (activeTab === "team")
      return (
        <section className="review-workspace__tab-panel review-workspace__team-page">
          <TeamPanel />
        </section>
      );
    return (
      <section className="review-workspace__tab-panel review-workspace__activity-page">
        <ActivityPanel />
      </section>
    );
  };
  return (
    <main className="review-workspace piers-page">
      <header className="review-workspace__header">
        <Link
          className="review-workspace__back"
          to={backPath || (Number(review.inspectionCycleId) > 0
            ? `/inspections/${review.inspectionCycleId}/preview`
            : "/review-tasks")}
        >
          <i className="bx bx-left-arrow-alt" aria-hidden="true" />{" "}
          {backLabel || (Number(review.inspectionCycleId) > 0 ? "Back to Inspection Cycle" : "Back to My Work")}
        </Link>
        <div className="review-workspace__heading">
          <div className="piers-page-header">
            <h1 className="piers-page-title">
              {reviewType}{" "}
              <em>{reviewStatus === "Awaiting Approval" ? "AWAITING SIGN-OFF" : reviewStatus.toUpperCase()}</em>
            </h1>
            <p>
              <span>
                Engagement: <b>{engagement}</b>
              </span>
              <span>
                Client: <b>{client}</b>
              </span>
              <span>
                Service: <b>{reviewType}</b>
              </span>
            </p>
          </div>
          <div className="review-workspace__header-actions">
            {isAwaitingApproval ? (
              canApproveReview && (
                <>
                  <button
                    className="review-workspace__continue"
                    type="button"
                    onClick={approveReview}
                  >
                    Approve Review <i className="bx bx-check-double" />
                  </button>
                  <button
                    className="review-workspace__return"
                    type="button"
                    onClick={returnForChanges}
                  >
                    Return for Changes <i className="bx bx-undo" />
                  </button>
                </>
              )
            ) : (
              <button
                className="review-workspace__continue"
                type="button"
                onClick={startReview}
                disabled={!checklistItems.length}
              >
                {reviewActionLabel}{" "}
                <i className="bx bx-right-arrow-alt" />
              </button>
            )}
            <button
              className="review-workspace__refresh piers-refresh-button"
              type="button"
              onClick={refreshWorkspace}
              disabled={refreshingWorkspace}
            >
              <i
                className={`bx bx-refresh${
                  refreshingWorkspace ? " bx-spin" : ""
                }`}
              />{" "}
              {refreshingWorkspace ? "Refreshing..." : "Refresh"}
            </button>
            <button
              className="review-workspace__close"
              type="button"
              aria-label="Close review workspace"
              onClick={closeAction}
            >
              Close
            </button>
          </div>
        </div>
      </header>
      {refreshingWorkspace && (
        <div
          className="review-workspace__refresh-overlay"
          role="status"
          aria-live="polite"
        >
          <div>
            <i className="bx bx-loader-alt bx-spin" />
            <b>Refreshing review</b>
            <span>
              Loading the latest checklists, queries, findings and documents…
            </span>
          </div>
        </div>
      )}
      <section className="review-workspace__metrics">
        <article className="review-workspace__metric review-workspace__metric--progress">
          <span
            className="review-workspace__ring"
            style={{ "--progress": `${progress * 3.6}deg` }}
          >
            <i />
          </span>
          <div>
            <small>Overall Progress</small>
            <b>{progress}%</b>
            <em>
              {answeredCount} of {checklistItems.length} answered
            </em>
          </div>
        </article>
        {[
          [
            "bx-check-circle",
            "green",
            "Sections Completed",
            sections.filter(
              (section) => section.complete === section.total && section.total
            ).length,
            `of ${sections.length}`,
            "Checklist areas",
          ],
          [
            "bx-message-rounded-dots",
            "orange",
            "Open Queries",
            queries.length,
            "",
            queries.length ? "Need your attention" : "No open queries",
          ],
          [
            "bx-flag",
            "purple",
            "Findings",
            findings.length,
            "",
            "Recorded so far",
          ],
          [
            "bx-paperclip",
            "blue",
            "Attachments",
            evidenceCount + reviewDocuments.length,
            "",
            "Total files",
          ],
          [
            "bx-calendar",
            "blue",
            "Due Date",
            dateLabel(review.dueDate),
            "",
            dueNote(review.dueDate),
          ],
        ].map(([icon, tone, label, value, suffix, note]) => (
          <article
            className={`review-workspace__metric review-workspace__metric--${tone}`}
            key={label}
          >
            <i className={`bx ${icon}`} />
            <div>
              <small>
                {label}
                {label === "Open Queries" && queries.length > 0 && (
                  <b className="review-workspace__metric-alert" />
                )}
              </small>
              <strong>
                {value} {suffix && <em>{suffix}</em>}
              </strong>
              <span>{note}</span>
            </div>
          </article>
        ))}
      </section>
      <nav
        className="review-workspace__tabs"
        aria-label="Review workspace sections"
      >
        {tabs.map(([id, label, count]) => (
          <button
            type="button"
            className={activeTab === id ? "active" : ""}
            key={id}
            onClick={() => setActiveTab(id)}
          >
            {label}
            {Number(count) > 0 && <b>{count}</b>}
          </button>
        ))}
      </nav>
      <div className="review-workspace__content">{TabContent()}</div>
      <Modal isOpen={assignmentMessageOpen} toggle={() => setAssignmentMessageOpen(false)} centered>
        <ModalBody>
          <h2 className="h5">Review assignment</h2>
          <p>This review is assigned to <strong>{review.assignedUser?.fullName || review.ownerUser?.fullName || users.find((user) => Number(user.id) === Number(review.assignedTo))?.fullName || "the assigned reviewer"}</strong>. Only the assigned reviewer can review the checklist or raise queries.</p>
          {canOpenReadOnly && <p>You can open this review in read-only mode. You cannot change scope, answers, evidence, queries, findings or submit the review.</p>}
          <div className="d-flex justify-content-end gap-2">
            <button type="button" className="btn btn-light" onClick={() => setAssignmentMessageOpen(false)}>Cancel</button>
            {canOpenReadOnly && <button type="button" className="btn btn-primary" onClick={() => { setAssignmentMessageOpen(false); openChecklistAction?.({ readOnly: true }); }}>Open Read-Only</button>}
          </div>
        </ModalBody>
      </Modal>
      <Modal
        centered
        isOpen={Boolean(workflowTaskToReassign)}
        toggle={() => setWorkflowTaskToReassign(null)}
      >
        <ModalBody className="review-workspace__task-reassign-modal">
          <h2>Reassign review task</h2>
          <p>{workflowTaskToReassign?.taskName || "Review task"}</p>
          <label>
            <span>Assign to</span>
            <select
              value={workflowAssignee}
              onChange={(event) => setWorkflowAssignee(event.target.value)}
            >
              <option value="">Select user</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.fullName}
                </option>
              ))}
            </select>
          </label>
          <small>
            This changes only the active task and records the change in the
            review activity timeline.
          </small>
          <footer>
            <button
              type="button"
              onClick={() => setWorkflowTaskToReassign(null)}
              disabled={reassigningWorkflowTask}
            >
              Cancel
            </button>
            <button
              type="button"
              className="is-primary"
              onClick={reassignWorkflowTask}
              disabled={!workflowAssignee || reassigningWorkflowTask}
            >
              {reassigningWorkflowTask ? "Reassigning..." : "Reassign Task"}
            </button>
          </footer>
        </ModalBody>
      </Modal>
      <Modal
        centered
        size="lg"
        isOpen={addChecklistsOpen}
        toggle={() => {
          if (!addingChecklists) setAddChecklistsOpen(false);
        }}
      >
        <ModalBody className="review-workspace__add-checklists-modal">
          <header>
            <div>
              <h2>Add Checklists</h2>
              <p>
                Select additional checklists for this review. Their questions
                will be assigned to the primary reviewer.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setAddChecklistsOpen(false)}
              disabled={addingChecklists}
              aria-label="Close"
            >
              <i className="bx bx-x" />
            </button>
          </header>
          <div className="review-workspace__available-checklists">
            {availableChecklists.length ? (
              availableChecklists.map((checklist) => {
                const workTypes =
                  (checklist.workTypes || [])
                    .map((item) => item.workType?.name || item.name)
                    .filter(Boolean)
                    .join(", ") || "All work types";
                const industryTypes =
                  (checklist.industryTypes || [])
                    .map((item) => item.industryType?.name || item.name)
                    .filter(Boolean)
                    .join(", ") || "All industries";
                const selected = selectedChecklistIds.includes(
                  Number(checklist.id)
                );
                return (
                  <label
                    className={selected ? "is-selected" : ""}
                    key={checklist.id}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleChecklistSelection(checklist.id)}
                      disabled={addingChecklists}
                    />
                    <span>
                      <b>{checklist.name}</b>
                      <small>
                        {checklist.standardType?.name ||
                          "No standard specified"}
                      </small>
                    </span>
                    <span>
                      <small>Work Type</small>
                      <b>{workTypes}</b>
                    </span>
                    <span>
                      <small>Industry Type</small>
                      <b>{industryTypes}</b>
                    </span>
                  </label>
                );
              })
            ) : (
              <div className="review-workspace__no-available-checklists">
                <i className="bx bx-check-circle" />
                <b>All available checklists are already assigned.</b>
              </div>
            )}
          </div>
          <footer>
            <span>{selectedChecklistIds.length} selected</span>
            <button
              type="button"
              onClick={() => setAddChecklistsOpen(false)}
              disabled={addingChecklists}
            >
              Cancel
            </button>
            <button
              type="button"
              className="is-primary"
              onClick={saveAddedChecklists}
              disabled={addingChecklists || !selectedChecklistIds.length}
            >
              {addingChecklists ? "Adding..." : "Add Checklists"}
            </button>
          </footer>
        </ModalBody>
      </Modal>
    </main>
  );
};

export default InspectionReviewWorkspace;
